import { useEffect, useState } from 'react';
import { Alert, Button, Group, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { CodeHighlight } from '@mantine/code-highlight';
import { KeyIcon, ShieldCheckIcon, WarningIcon } from '@phosphor-icons/react';
import { type DecodedJwt, decodeJwt, verifyHs256 } from '@/lib/jwt';
import { useStrings } from '@/i18n/useLang';

export function JwtTool() {
  const t = useStrings();
  const [token, setToken] = useState('');
  const [decoded, setDecoded] = useState<DecodedJwt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState('');
  const [verified, setVerified] = useState<boolean | null>(null);

  useEffect(() => {
    setVerified(null);
    if (!token.trim()) {
      setDecoded(null);
      setError(null);
      return;
    }
    try {
      setDecoded(decodeJwt(token));
      setError(null);
    } catch (e) {
      setDecoded(null);
      setError((e as Error).message);
    }
  }, [token]);

  const verify = async () => {
    try {
      setVerified(await verifyHs256(token, secret));
    } catch {
      setVerified(false);
    }
  };

  return (
    <Stack gap="md">
      <Textarea
        label={t.ui.token}
        placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.…"
        value={token}
        onChange={(e) => setToken(e.currentTarget.value)}
        autosize
        minRows={3}
        maxRows={6}
        styles={{ input: { fontFamily: 'monospace' } }}
      />
      {error && (
        <Alert color="red" icon={<WarningIcon size={18} />} variant="light">
          {error}
        </Alert>
      )}
      {decoded && (
        <Stack gap="md">
          <div>
            <Text fw={600} size="sm" mb={4}>
              {t.ui.header}
            </Text>
            <CodeHighlight code={JSON.stringify(decoded.header, null, 2)} language="json" className="code-wrap" />
          </div>
          <div>
            <Text fw={600} size="sm" mb={4}>
              {t.ui.payload}
            </Text>
            <CodeHighlight code={JSON.stringify(decoded.payload, null, 2)} language="json" className="code-wrap" />
          </div>
          <div>
            <Text fw={600} size="sm" mb={6}>
              {t.ui.verify}
            </Text>
            <Group align="flex-end">
              <TextInput label={t.ui.secret} value={secret} onChange={(e) => setSecret(e.currentTarget.value)} style={{ flex: 1 }} />
              <Button leftSection={<KeyIcon size={16} />} onClick={verify} disabled={!secret}>
                {t.ui.verifyBtn}
              </Button>
            </Group>
            {verified !== null &&
              (verified ? (
                <Alert color="green" icon={<ShieldCheckIcon size={18} />} variant="light" mt="sm">
                  {t.ui.verifyOk}
                </Alert>
              ) : (
                <Alert color="red" icon={<WarningIcon size={18} />} variant="light" mt="sm">
                  {t.ui.verifyFail}
                </Alert>
              ))}
          </div>
        </Stack>
      )}
    </Stack>
  );
}
